<script>
/*
This whole file has a tricky workaround to handle UTC and GMT differences.
The main reason is that the calendar element ui is based in UTC, but when you get a value from it, it turns it into GMT.

For example: If you select a random date such as 15/07, the calendar element guess it's `2022-07-15T00:00:00.000Z` but when
you save the value in a prop it saves as `2022-07-14T21:00:00.000 GMT-0300`.

It's a reported bug, you can find the demos in their webpage bugging https://element.eleme.io/#/es/component/calendar
(Click on a date in Basic Calendar demo and see what happens)

I've marked the 'tricky' parts with a comment to identify faster those parts. Find the word 'workaround'.
*/
import Spinner from "@/components/ui/Spinner";
import slotService from "@/services/slot";
import moment from "moment";
import { mapGetters } from "vuex";
import { isAssistantHistoricalAgendaEnabled } from "@/services/config";

export default {
  name: "slotsCalendar",

  props: {
    selectedDoctorId: {
      type: String,
      required: true,
    },
    selectedInstitutionId: {
      type: String,
      required: true,
    },
    selectedMedicalSpecialtyId: {
      type: String,
      required: true,
    },
    isHL7AgendaEnabled: {
      type: Boolean,
      default: false,
    },
  },

  components: {
    Spinner,
  },

  data() {
    return {
      selectedDate: "",
      selectedMonth: "",
      slots: [],
      isloadingSlots: false,
    };
  },

  computed: {
    ...mapGetters(["isAssistant", "isDoctor", "isAdmin"]),

    canNavigatePastDates() {
      return (
        isAssistantHistoricalAgendaEnabled &&
        (this.isAssistant || this.isDoctor || this.isAdmin)
      );
    },
  },

  created() {
    const now = moment();

    // workaround
    const offset = moment().utcOffset() * -1;
    this.selectedDate = now.subtract(offset, "minutes").toDate();
    // workaround

    this.selectedMonth = now.utc().month() + 1;
  },

  watch: {
    selectedDate() {
      // workaround
      this.selectedMonth = moment(this.selectedDate).utc().month() + 1;
      // workaround

      if (this.selectedDate) {
        this.$bus.$emit("selected-date", this.selectedDate);
      }
    },

    selectedMonth() {
      this.getSlotsAvailability();
    },

    selectedDoctorId() {
      this.slots = [];
      this.getSlotsAvailability();
    },

    selectedMedicalSpecialtyId() {
      this.slots = [];
      this.getSlotsAvailability();
    },

    selectedInstitutionId() {
      this.slots = [];
      this.getSlotsAvailability();
    },

    isHL7AgendaEnabled() {
      this.slots = [];
      this.getSlotsAvailability();
    },
  },

  methods: {
    async getSlotsAvailability() {
      const hasRequiredFilters =
        this.selectedInstitutionId &&
        this.selectedDoctorId &&
        (this.selectedMedicalSpecialtyId || this.isHL7AgendaEnabled);

      if (!hasRequiredFilters) return;

      this.isloadingSlots = true;
      this.slots = [];

      try {
        const startDate = this.getDateFirstDayOfMonth(this.selectedDate);
        const endDate = this.getDateLastDayOfMonth(this.selectedDate);
        const now = moment().format();

        if (endDate <= now) return;

        if (this.isHL7AgendaEnabled) {
          const res = await slotService.getSlotsByDoctor({
            doctorId: this.selectedDoctorId,
            params: {
              startsAt: { $gte: startDate },
              endsAt: { $lte: endDate },
              limit: 1000,
            },
          });
          this.slots = this.groupSlotsByDate(
            res.docs.map((slot) => ({
              startsAt: slot.startsAt,
              available: slot.available ? 1 : 0,
            }))
          );
          return;
        }

        const query = {
          doctor: this.selectedDoctorId,
          medicalSpecialties: this.selectedMedicalSpecialtyId,
          institution: this.selectedInstitutionId,
          endDate,
          startDate,
        };

        const fetchedSlots = await slotService.getSlotsAvailability(query);
        this.slots = this.groupSlotsByDate(fetchedSlots);
      } finally {
        this.isloadingSlots = false;
      }
    },

    groupSlotsByDate(slots) {
      const groupedSlots = [];

      slots.reduce((acc, currentElem) => {
        const date = moment(currentElem.startsAt).format("YYYY-MM-DD");

        if (!acc[date]) {
          acc[date] = { date, availableSlots: 0 };
          groupedSlots.push(acc[date]);
        }

        acc[date].availableSlots += currentElem.available;
        return acc;
      }, {});

      return groupedSlots;
    },

    getDateFirstDayOfMonth(date) {
      // workaround
      const firstDay = moment(date)
        .startOf("month")
        .utc()
        .format("YYYY-MM-DDTHH:mm:ss.SSSZ");

      return firstDay;
      // workaround
    },

    getDateLastDayOfMonth(date) {
      // workaround
      const lastDay = moment(date)
        .endOf("month")
        .utc()
        .format("YYYY-MM-DDTHH:mm:ss.SSSZ");

      return lastDay;
      // workaround
    },

    getDay(date) {
      const day = date.split("-");
      return day[2];
    },

    isDayAvailable(day) {
      if (typeof day === "object") {
        day = day.toISOString().split("T")[0];
      } else {
        day = day.split("T")[0];
      }

      const today = moment().utc().startOf("day");
      const currentDay = moment(day).utc().startOf("day");
      const isPast = currentDay.isBefore(today, "day");

      const [daySlots] = this.slots.filter((slot) => slot.date === day);

      if (!daySlots) return isPast ? "past-date" : "without-agenda";

      return daySlots.availableSlots >= 1 ? "available" : "not-available";
    },

    isPastDate(date) {
      // workaround - normalizar a UTC como en el resto del código
      const today = moment().utc().startOf("day");
      const selectedDay = moment(date).utc().startOf("day");
      return selectedDay.isBefore(today, "day");
    },

    handleCellClick(event, day) {
      // Evitar el click en fechas pasadas, salvo que el usuario tenga
      // habilitada la navegación histórica (Asistente en Deragopyan).
      if (this.isPastDate(day) && !this.canNavigatePastDates) {
        event.stopPropagation();
        event.preventDefault();
        return false;
      }
    },

    isCellSelected(isSelected, day) {
      if (isSelected) return true;
      if (!this.selectedDate) return false;
      const dayStr = (
        typeof day === "object" ? day.toISOString() : String(day)
      ).split("T")[0];
      const selectedStr = moment(this.selectedDate).utc().format("YYYY-MM-DD");
      return dayStr === selectedStr;
    },

    setSelectedDate(date) {
      // Evitar seleccionar fechas pasadas, salvo navegación histórica habilitada.
      if (this.isPastDate(date) && !this.canNavigatePastDates) {
        return;
      }

      const offset = moment().utcOffset() * -1;

      const correctedDate = moment(date)
        .add(offset, "minutes")
        .format("YYYY-MM-DDTHH:mm:ss.SSSZ");

      this.selectedDate = correctedDate;
    },
  },
};
</script>

<template lang="pug">
  .calendar
    spinner(v-if="isloadingSlots")
    el-calendar(:first-day-of-week="7" @input="setSelectedDate" format="yyyy/MM/dd" disabled=true)
      template(slot="dateCell" slot-scope="{date, data}")
        div(
          :class="[isDayAvailable(data.day), (isPastDate(data.day) && canNavigatePastDates) ? 'past-date--navigable' : '', (isCellSelected(data.isSelected, data.day) && (!isPastDate(data.day) || canNavigatePastDates)) ? 'is-selected' : '', 'slot-cell']"
          @click="handleCellClick($event, data.day)"
          @mousedown="handleCellClick($event, data.day)"
        )
          span {{ getDay(data.day) }}
</template>

<style lang="scss" scoped>
.calendar {
  position: relative;
}

.slot-cell {
  padding: 8px;
  width: 100%;
  height: 100%;
}

.without-agenda {
  cursor: default;
}

.available {
  background-color: lightgreen !important;
}

.not-available {
  background-color: lightgreen !important;
}

.past-date {
}

.past-date--navigable {
}

.is-selected {
  color: white;
  box-shadow: inset 0 0 0 1000px rgba(0, 0, 0, 0.2);
}
</style>
